"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

export type ProjectionType =
  | "equirectangular_360"
  | "equirectangular_180"
  | "fisheye_180"
  | "mkx200"
  | "mkx220"
  | "flat"

export type StereoLayout = "mono" | "sbs" | "tb"

export type VRAudioMode = "original" | "enhanced" | "front" | "positioned" | "upmix" | "ambisonic"
export type VRAmbisonicOrder = "ambix" | "fuma"

export interface VRAudioSettings {
  mode: VRAudioMode
  boostDb: number
  bass: number
  mid: number
  treble: number
  balance: number
  stereoWidth: number
  crossfeed: number
  spatialMix: number
  headTracking: boolean
  azimuth: number
  elevation: number
  distance: number
  rolloff: number
  roomMix: number
  roomSize: number
  upmixSpread: number
  upmixRear: number
  ambisonicOrder: VRAmbisonicOrder
}

export interface VRPreset {
  id: string
  name: string
  isBuiltIn: boolean
  pitch: number
  roll: number
  yaw: number
  fov: number
  ipd: number
  convergence: number
  height: number
}

export interface GreenscreenSettings {
  enabled: boolean
  color: string // hex
  threshold: number // 0-100, how close to the key color counts as "key" — similarity/tolerance
  smoothness: number // 0-100, soft edge band beyond the threshold instead of a hard cutoff
  spill: number // 0-100, pulls back key-color bleed on the remaining edge pixels
}

const DEFAULT_GREENSCREEN: GreenscreenSettings = { enabled: false, color: "#00ff00", threshold: 40, smoothness: 0, spill: 0 }
const MAX_KEY_COLOR_PRESETS = 4

export interface VRTransform {
  pitch: number       // degrees, -180 to 180
  roll: number        // degrees, -180 to 180 (KEY for lying down)
  yaw: number         // degrees, -180 to 180
  fov: number         // degrees, 60-120
  ipd: number         // mm, 58-72
  convergence: number // -0.1 to 0.1, per-eye horizontal UV shift (0 = no adjustment)
  height: number      // vertical offset, -1 to 1
  positionX: number   // horizontal offset, -3 to 3
  brightness: number  // 0-200 (100 = normal)
  contrast: number    // 0-200 (100 = normal)
  saturation: number  // 0-200 (100 = normal)
  sharpness: number       // 0-100 (0 = off)
  depthStrength: number   // 0.25-1.75 (1 = unscaled stereo separation)
  verticalAlignment: number // -0.1 to 0.1, per-eye vertical nudge (fixes misaligned SBS/TB rips)
  swapEyes: boolean
  bulge: number        // -2 to 2, pincushion/barrel-style radial warp, independent of the fisheye lens profiles
  screenDistance: number // meters, "flat" projection only
  screenScale: number    // 0.5-2, "flat" projection only
  curvature: number      // 0-1, "flat" projection only — 0 = flat plane, 1 = wrapped
}

const DEFAULT_TRANSFORM: VRTransform = {
  pitch: 0,
  roll: 0,
  yaw: 0,
  fov: 90,
  ipd: 64,
  convergence: 0,
  height: 0,
  positionX: 0,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  sharpness: 0,
  depthStrength: 1,
  verticalAlignment: 0,
  swapEyes: false,
  bulge: 0,
  screenDistance: 8,
  screenScale: 1,
  curvature: 0,
}

export const DEFAULT_VR_AUDIO: VRAudioSettings = {
  mode: "original",
  boostDb: 0,
  bass: 0,
  mid: 0,
  treble: 0,
  balance: 0,
  stereoWidth: 1,
  crossfeed: 0,
  spatialMix: 1,
  headTracking: true,
  azimuth: 0,
  elevation: 0,
  distance: 2,
  rolloff: 0.5,
  roomMix: 0,
  roomSize: 0.35,
  upmixSpread: 55,
  upmixRear: 0.18,
  ambisonicOrder: "ambix",
}

const BUILT_IN_PRESETS: VRPreset[] = [
  { id: "upright", name: "Upright Seated", isBuiltIn: true, pitch: 0, roll: 0, yaw: 0, fov: 90, ipd: 64, convergence: 0, height: 0 },
  { id: "reclined", name: "Reclined (Couch)", isBuiltIn: true, pitch: -20, roll: 0, yaw: 0, fov: 90, ipd: 64, convergence: 0, height: 0 },
  { id: "back", name: "Lying on Back", isBuiltIn: true, pitch: -5, roll: 90, yaw: 0, fov: 90, ipd: 64, convergence: 0, height: 0 },
  { id: "left", name: "Lying on Left Side", isBuiltIn: true, pitch: 0, roll: 90, yaw: 0, fov: 90, ipd: 64, convergence: 0, height: 0 },
  { id: "right", name: "Lying on Right Side", isBuiltIn: true, pitch: 0, roll: -90, yaw: 0, fov: 90, ipd: 64, convergence: 0, height: 0 },
  { id: "cinema", name: "Flat Cinema Screen", isBuiltIn: true, pitch: 0, roll: 0, yaw: 0, fov: 70, ipd: 64, convergence: 0, height: 0 },
]

interface VRStore {
  // Active state
  isVRMode: boolean
  projectionType: ProjectionType
  stereoLayout: StereoLayout

  // Transform
  transform: VRTransform

  // Passthrough + greenscreen
  passthrough: boolean
  greenscreen: GreenscreenSettings
  keyColorPresets: string[] // saved custom key colors, up to MAX_KEY_COLOR_PRESETS
  audio: VRAudioSettings
  audioAvailable: boolean | null

  // Presets
  presets: VRPreset[]
  activePresetId: string | null

  // Per-video saved settings (keyed by URL hash or filename)
  videoSettings: Record<string, { projectionType: ProjectionType; stereoLayout: StereoLayout; transform: VRTransform }>

  // Whether XR is supported in this browser
  xrSupported: boolean | null

  // Actions
  setVRMode: (active: boolean) => void
  setProjectionType: (type: ProjectionType) => void
  setStereoLayout: (layout: StereoLayout) => void
  setTransform: (partial: Partial<VRTransform>) => void
  resetTransform: () => void
  applyPreset: (presetId: string) => void
  savePreset: (name: string) => void
  deletePreset: (presetId: string) => void
  setPassthrough: (enabled: boolean) => void
  setGreenscreen: (settings: Partial<GreenscreenSettings>) => void
  saveKeyColorPreset: (color: string) => void
  deleteKeyColorPreset: (color: string) => void
  setAudio: (settings: Partial<VRAudioSettings>) => void
  setAudioAvailable: (available: boolean | null) => void
  applyAudioPreset: (presetId: "original" | "clear" | "immersive" | "upmix" | "close" | "room") => void
  saveVideoSettings: (key: string) => void
  loadVideoSettings: (key: string) => boolean
  setXRSupported: (supported: boolean) => void
}

export const useVRStore = create<VRStore>()(
  persist(
    (set, get) => ({
      isVRMode: false,
      projectionType: "equirectangular_180",
      stereoLayout: "mono",
      transform: { ...DEFAULT_TRANSFORM },
      passthrough: false,
      greenscreen: { ...DEFAULT_GREENSCREEN },
      keyColorPresets: [],
      audio: { ...DEFAULT_VR_AUDIO },
      audioAvailable: null,
      presets: [...BUILT_IN_PRESETS],
      activePresetId: null,
      videoSettings: {},
      xrSupported: null,

      setVRMode: (active) => set({ isVRMode: active }),

      setProjectionType: (type) => set({ projectionType: type }),

      setStereoLayout: (layout) => set({ stereoLayout: layout }),

      setTransform: (partial) =>
        set((state) => ({
          transform: { ...state.transform, ...partial },
          activePresetId: null, // clear preset selection when manually adjusting
        })),

      resetTransform: () =>
        set({ transform: { ...DEFAULT_TRANSFORM }, activePresetId: null }),

      applyPreset: (presetId) => {
        const preset = get().presets.find((p) => p.id === presetId)
        if (!preset) return
        set({
          transform: {
            ...get().transform,
            pitch: preset.pitch,
            roll: preset.roll,
            yaw: preset.yaw,
            fov: preset.fov,
            ipd: preset.ipd,
            convergence: preset.convergence,
            height: preset.height,
          },
          activePresetId: presetId,
        })
      },

      savePreset: (name) => {
        const { transform } = get()
        const newPreset: VRPreset = {
          id: `custom_${Date.now()}`,
          name,
          isBuiltIn: false,
          pitch: transform.pitch,
          roll: transform.roll,
          yaw: transform.yaw,
          fov: transform.fov,
          ipd: transform.ipd,
          convergence: transform.convergence,
          height: transform.height,
        }
        set((state) => ({
          presets: [...state.presets, newPreset],
          activePresetId: newPreset.id,
        }))
      },

      deletePreset: (presetId) =>
        set((state) => ({
          presets: state.presets.filter((p) => p.id !== presetId || p.isBuiltIn),
          activePresetId: state.activePresetId === presetId ? null : state.activePresetId,
        })),

      setPassthrough: (enabled) => set({ passthrough: enabled }),

      setGreenscreen: (settings) =>
        set((state) => ({
          greenscreen: { ...state.greenscreen, ...settings },
        })),

      saveKeyColorPreset: (color) =>
        set((state) => {
          const next = [color, ...state.keyColorPresets.filter((c) => c !== color)].slice(0, MAX_KEY_COLOR_PRESETS)
          return { keyColorPresets: next }
        }),

      deleteKeyColorPreset: (color) =>
        set((state) => ({ keyColorPresets: state.keyColorPresets.filter((c) => c !== color) })),

      setAudio: (settings) =>
        set((state) => ({ audio: { ...state.audio, ...settings } })),

      setAudioAvailable: (available) => set({ audioAvailable: available }),

      applyAudioPreset: (presetId) => {
        const presets: Record<typeof presetId, VRAudioSettings> = {
          original: { ...DEFAULT_VR_AUDIO },
          clear: { ...DEFAULT_VR_AUDIO, mode: "enhanced", boostDb: 2, mid: 2, treble: 2, stereoWidth: 1.05, crossfeed: 0.04 },
          immersive: { ...DEFAULT_VR_AUDIO, mode: "front", boostDb: 1, bass: 2, stereoWidth: 1.15, crossfeed: 0.08, spatialMix: 0.85, roomMix: 0.12, roomSize: 0.45 },
          upmix: { ...DEFAULT_VR_AUDIO, mode: "upmix", boostDb: 1, bass: 1, stereoWidth: 1.1, upmixSpread: 55, upmixRear: 0.18, roomMix: 0.14, roomSize: 0.55 },
          close: { ...DEFAULT_VR_AUDIO, mode: "front", boostDb: 3, bass: 1, mid: 1.5, spatialMix: 0.9, distance: 0.65, rolloff: 0.2, roomMix: 0.03 },
          room: { ...DEFAULT_VR_AUDIO, mode: "front", boostDb: 1, bass: 1, stereoWidth: 1.1, crossfeed: 0.12, spatialMix: 0.75, distance: 2.5, rolloff: 0.65, roomMix: 0.32, roomSize: 0.7 },
        }
        set({ audio: presets[presetId] })
      },

      saveVideoSettings: (key) => {
        const { projectionType, stereoLayout, transform } = get()
        set((state) => ({
          videoSettings: {
            ...state.videoSettings,
            [key]: { projectionType, stereoLayout, transform: { ...transform } },
          },
        }))
      },

      loadVideoSettings: (key) => {
        const saved = get().videoSettings[key]
        if (!saved) return false
        set({
          projectionType: saved.projectionType,
          stereoLayout: saved.stereoLayout,
          transform: { ...saved.transform },
        })
        return true
      },

      setXRSupported: (supported) => set({ xrSupported: supported }),
    }),
    {
      name: "goonru-vr-store",
      version: 6,
      migrate: (persisted, version) => {
        const s = persisted as Partial<VRStore>
        if (version < 2) {
          // Reset stereoLayout — old default was "sbs" which caused confusion on mono content
          s.stereoLayout = "mono"
        }
        s.audio = { ...DEFAULT_VR_AUDIO, ...(s.audio ?? {}) }
        // v6 added smoothness/spill to greenscreen — back fill so old saved state doesn't
        // leave their shader uniforms (and sliders) undefined.
        s.greenscreen = { ...DEFAULT_GREENSCREEN, ...(s.greenscreen ?? {}) }
        s.keyColorPresets = s.keyColorPresets ?? []
        // v5 added sharpness/depthStrength/verticalAlignment/swapEyes/bulge/screenDistance/
        // screenScale/curvature/positionX, and repurposed convergence's scale (was 0-2
        // "screen-space", unused by any shader; now a small ±0.1 UV shift). A saved transform
        // missing the new keys would otherwise leave their shader uniforms undefined (NaN) and
        // their sliders NaN. A convergence value outside the new ±0.1 range is unmistakably the
        // stale large-scale one, not a real ±0.1 user value someone would have picked — resets
        // to the new neutral default rather than feeding a shader-breaking magnitude through.
        const t = s.transform as (Partial<VRTransform> & { convergence?: number }) | undefined
        s.transform = {
          ...DEFAULT_TRANSFORM,
          ...(t ?? {}),
          convergence: t?.convergence != null && Math.abs(t.convergence) <= 0.1 ? t.convergence : DEFAULT_TRANSFORM.convergence,
        }
        return s as VRStore
      },
      partialize: (state) => ({
        projectionType: state.projectionType,
        stereoLayout: state.stereoLayout,
        transform: state.transform,
        passthrough: state.passthrough,
        greenscreen: state.greenscreen,
        keyColorPresets: state.keyColorPresets,
        audio: state.audio,
        presets: state.presets,
        videoSettings: state.videoSettings,
      }),
    },
  ),
)
